<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

// inboxesGetter/title: reuso no tab de SMS (mesmo form de drip, filtrando inboxes
// shortcode). Default = WhatsApp/WAHA. Agora é um MODAL CENTRAL (DS Dialog), aberto
// via ref().open() — mais espaço pra gestão e pode ser disparado de qualquer botão.
const props = defineProps({
  inboxesGetter: {
    type: String,
    default: 'inboxes/getWhatsappCampaignInboxes',
  },
  title: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const confirmCloseRef = ref(null);
const formRef = ref(null);

const addCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    // Disparo em massa v2 (drip): a campanha é criada como RASCUNHO (não inicia
    // sozinha). O usuário revisa, faz a prévia e clica "Iniciar" no card.
    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, { type: CAMPAIGN_TYPES.ONE_OFF });
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// O form emite 'submit' e em seguida 'cancel' (fecha). addCampaign roda em background.
const handleSubmit = campaignDetails => addCampaign(campaignDetails);
const handleCancel = () => dialogRef.value?.close();

// Clicar fora / ESC: se há algo preenchido, confirma antes de descartar o form.
const handleRequestClose = () => {
  if (formRef.value?.isDirty?.()) {
    confirmCloseRef.value?.open();
  } else {
    dialogRef.value?.close();
  }
};
const confirmAbandon = () => {
  confirmCloseRef.value?.close();
  dialogRef.value?.close();
};

defineExpose({
  open: () => dialogRef.value?.open(),
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title || t('CAMPAIGN.WHATSAPP.CREATE.TITLE')"
    width="3xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    confirm-before-close
    @request-close="handleRequestClose"
  >
    <WhatsAppCampaignForm
      ref="formRef"
      :inboxes-getter="inboxesGetter"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
  </Dialog>

  <Dialog
    ref="confirmCloseRef"
    type="alert"
    :title="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.TITLE')"
    :description="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.CONFIRM')"
    :cancel-button-label="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.CANCEL')"
    @confirm="confirmAbandon"
  />
</template>
